import { useState, useCallback, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { PATH_URL } from 'src/helpers/path'
import { getQueryParameters } from 'src/helpers/tool'
import { preserveUrlParams } from 'src/helpers/preserveUrlParams'
import { UseGuestCountReturn } from './types'
import { validateGuestCount } from './utils'

export const useGuestCount = (): UseGuestCountReturn => {
  const [guestCount, setGuestCount] = useState('')
  const [error, setError] = useState('')
  const [isValid, setIsValid] = useState(false)
  const history = useHistory()

  // Check for collectionId parameter and save it to collectionsQuizEntry
  useEffect(() => {
    const params = getQueryParameters()
    const collectionId = params.get('collectionId')

    if (collectionId) {
      console.log('Collection ID detected in GuestCountPage, saving to collectionsQuizEntry')
      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
      collectionsQuizEntry.collectionId = collectionId
      localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))
    }
  }, [])

  const handleGuestCountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '')
    setGuestCount(value)

    const validation = validateGuestCount(value)
    setError(validation.error)
    setIsValid(validation.isValid)
  }, [])

  const handleSubmitGuestCount = useCallback(() => {
    const validation = validateGuestCount(guestCount)

    if (!validation.isValid) {
      setError(validation.error)
      setIsValid(false)
      return
    }

    try {
      const collectionsQuizEntry = localStorage.getItem('collectionsQuizEntry')
      if (!collectionsQuizEntry) {
        console.warn('collectionsQuizEntry not found in localStorage')
        return
      }
      const currentData = JSON.parse(collectionsQuizEntry)

      const quizResponses = currentData.quizResponses || []
      const guestCountIndex = quizResponses.findIndex((item: any) => item.key === 'GUEST_COUNT')
      const updatedQuizResponses =
        guestCountIndex > -1
          ? quizResponses.map((item: any, index: number) =>
              index === guestCountIndex ? { ...item, values: guestCount } : item
            )
          : [...quizResponses, { key: 'GUEST_COUNT', values: guestCount }]

      currentData.quizResponses = updatedQuizResponses

      // Ensure collectionId is saved if present
      const params = getQueryParameters()
      const collectionId = params.get('collectionId')
      if (collectionId) {
        currentData.collectionId = collectionId
      }

      localStorage.setItem('collectionsQuizEntry', JSON.stringify(currentData))

      // Use preserveUrlParams to maintain query parameters
      const nextUrl = preserveUrlParams(PATH_URL.COLLECTIONS_QUIZ_EXPECTED_BUDGET)
      history.push(nextUrl)
    } catch (err) {
      console.warn('Failed to update guest count in localStorage:', err)
    }
  }, [guestCount, history])

  const handleSkip = useCallback(() => {
    try {
      const collectionsQuizEntry = localStorage.getItem('collectionsQuizEntry')
      if (!collectionsQuizEntry) {
        console.warn('collectionsQuizEntry not found in localStorage')
        return
      }
      const currentData = JSON.parse(collectionsQuizEntry)

      const quizResponses = currentData.quizResponses || []
      const guestCountIndex = quizResponses.findIndex((item: any) => item.key === 'GUEST_COUNT')
      const updatedQuizResponses =
        guestCountIndex > -1
          ? quizResponses.map((item: any, index: number) =>
              index === guestCountIndex ? { ...item, values: '' } : item
            )
          : [...quizResponses, { key: 'GUEST_COUNT', values: '' }]

      currentData.quizResponses = updatedQuizResponses

      // Ensure collectionId is saved if present
      const params = getQueryParameters()
      const collectionId = params.get('collectionId')
      if (collectionId) {
        currentData.collectionId = collectionId
      }

      localStorage.setItem('collectionsQuizEntry', JSON.stringify(currentData))

      // Use preserveUrlParams to maintain query parameters
      const nextUrl = preserveUrlParams(PATH_URL.COLLECTIONS_QUIZ_EXPECTED_BUDGET)
      history.push(nextUrl)
    } catch (err) {
      console.warn('Failed to skip guest count in localStorage:', err)
    }
  }, [history])

  return {
    guestCount,
    error,
    isValid,
    handleGuestCountChange,
    handleSubmitGuestCount,
    handleSkip,
  }
}
